import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import productData from "../Datas/productData";
import { formatCurrency } from "../config/siteConfig";
import "../css/main.scss";

const heroSlides = [
  {
    image: "/main/hero-stationery.jpg",
    eyebrow: "Everyday instruments",
    title: "Write it down.",
    text: "Well-made tools turn a passing thought into something you can keep.",
    button: "Shop writing tools",
    to: "/shop?category=Pens",
    placement: "left",
  },
  {
    image: "/main/hero-letter-writing.jpg",
    eyebrow: "Analog correspondence",
    title: "To the letter",
    text: "Slow down, choose your words, and send something made by hand.",
    button: "Shop letter sets",
    to: "/shop?subcategory=Letter%20Sets",
    placement: "left",
  },
  {
    image: "/main/hero-quiet-color.jpg",
    eyebrow: "Quiet color",
    title: "Make a soft mark",
    text: "A calmer palette for study notes, plans, and work in progress.",
    button: "Shop highlighters",
    to: "/shop?subcategory=Highlighters",
    placement: "right",
  },
];

const brandCollections = productData.reduce((collections, product) => {
  if (collections.some((collection) => collection.name === product.brand)) {
    return collections;
  }

  collections.push({
    name: product.brand,
    image: product.image,
  });
  return collections;
}, []);

const Main = () => {
  const productRailRef = useRef(null);
  const brandRailRef = useRef(null);
  const heroTouchRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);

  const featuredProducts = useMemo(
    () => productData.filter((product) => product.featured).slice(0, 10),
    []
  );

  useEffect(() => {
    if (heroPaused) return undefined;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 7000);
    return () => window.clearInterval(interval);
  }, [heroPaused]);

  const changeSlide = (direction) => {
    setActiveSlide(
      (current) =>
        (current + direction + heroSlides.length) % heroSlides.length
    );
  };

  const startHeroSwipe = (event) => {
    const touch = event.touches[0];
    heroTouchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const endHeroSwipe = (event) => {
    if (!heroTouchRef.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - heroTouchRef.current.x;
    const deltaY = touch.clientY - heroTouchRef.current.y;
    heroTouchRef.current = null;
    if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
      changeSlide(deltaX < 0 ? 1 : -1);
    }
  };

  const scrollProducts = (direction) => {
    const rail = productRailRef.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * rail.clientWidth * 0.8, behavior: "smooth" });
  };

  const scrollBrands = (direction) => {
    const rail = brandRailRef.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * rail.clientWidth * 0.8, behavior: "smooth" });
  };

  const journalStories = [
    {
      image: "/main/story-letter-ritual.jpg",
      title: "A small ritual for better letters",
      text: "Make space, choose a good pen, and write the thing you would rather not leave to a screen.",
    },
    {
      image: "/main/story-desk-tools.jpg",
      title: "How to build an everyday writing kit",
      text: "Three useful pieces are enough: a dependable pen, receptive paper, and somewhere to keep both close.",
    },
    {
      image: "/main/story-field-paint.jpg",
      title: "A compact palette for working outside",
      text: "A small set of dependable colors can make field sketches feel spontaneous without becoming chaotic.",
    },
  ];

  return (
    <main className="home-page" id="main-content">
      <section
        className="home-hero ink-container ink-container--mobile-bleed"
        aria-label="Featured collections"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
        onFocusCapture={() => setHeroPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setHeroPaused(false);
        }}
      >
        <div
          className="home-hero__viewport"
          onTouchStart={startHeroSwipe}
          onTouchEnd={endHeroSwipe}
        >
          {heroSlides.map((slide, index) => (
            <article
              className={`home-hero__slide ${index === activeSlide ? "is-active" : ""}`}
              key={slide.title}
              aria-hidden={index !== activeSlide}
            >
              <img src={slide.image} alt="" />
              <div className={`home-hero__copy home-hero__copy--${slide.placement.replace(" ", "-")}`}>
                <p>{slide.eyebrow}</p>
                <h1>{slide.title}</h1>
                <span>{slide.text}</span>
                <Link to={slide.to} tabIndex={index === activeSlide ? undefined : -1}>
                  {slide.button}
                </Link>
              </div>
            </article>
          ))}
          <button
            type="button"
            className="home-hero__arrow home-hero__arrow--prev"
            onClick={() => changeSlide(-1)}
            aria-label="Previous slide"
          >
            <FiChevronLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            className="home-hero__arrow home-hero__arrow--next"
            onClick={() => changeSlide(1)}
            aria-label="Next slide"
          >
            <FiChevronRight aria-hidden="true" />
          </button>
          <div className="home-hero__dots" aria-label="Choose a slide">
            {heroSlides.map((slide, index) => (
              <button
                type="button"
                key={slide.title}
                className={index === activeSlide ? "is-active" : ""}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}: ${slide.title}`}
                aria-current={index === activeSlide ? "true" : undefined}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="home-intro ink-container">
        <h2>The tools we keep</h2>
        <p>
          The right instrument makes ordinary work feel considered. We choose
          stationery that is useful, beautiful, and ready to earn a permanent
          place on your desk.
        </p>
      </section>

      <section className="home-promos ink-container ink-container--mobile-bleed" aria-label="Shop featured categories">
        <Link className="home-promo" to="/shop?subcategory=Highlighters">
          <img src="/main/highlighter-story.jpg" alt="Pastel highlighters on an open notebook" loading="lazy" />
          <span className="home-promo__copy">
            <small>Gentle emphasis</small>
            <strong>Color that works with the page</strong>
            <em>Shop highlighters</em>
          </span>
        </Link>
        <Link className="home-promo" to="/shop?subcategory=Letter%20Sets">
          <img src="/main/letter-writing.jpg" alt="Cream letter paper, blue envelopes, and a fountain pen" loading="lazy" />
          <span className="home-promo__copy">
            <small>Analog living</small>
            <strong>Send a note they can keep</strong>
            <em>Shop letter sets</em>
          </span>
        </Link>
      </section>

      <section className="home-products" aria-labelledby="editor-picks-title">
        <div className="home-section-heading ink-container">
          <button type="button" onClick={() => scrollProducts(-1)} aria-label="Previous editor picks">
            <FiChevronLeft aria-hidden="true" />
          </button>
          <div>
            <h2 id="editor-picks-title">Editor&apos;s top picks</h2>
            <Link to="/shop">View all</Link>
          </div>
          <button type="button" onClick={() => scrollProducts(1)} aria-label="Next editor picks">
            <FiChevronRight aria-hidden="true" />
          </button>
        </div>
        <div className="home-product-rail" ref={productRailRef}>
          {featuredProducts.map((product) => (
            <article className="home-product-card" key={product.id}>
              <Link className="home-product-card__image" to={`/shopDetails/${product.id}`}>
                <img src={product.images[0]} alt={product.name} loading="lazy" />
                <img src={product.images[1]} alt="" loading="lazy" aria-hidden="true" />
                {product.stock === 0 && <span>Out of stock</span>}
              </Link>
              <Link className="home-product-card__info" to={`/shopDetails/${product.id}`}>
                <span>{product.name}</span>
                <small>{formatCurrency(product.price)}</small>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="home-makers" aria-labelledby="maker-title">
        <div className="home-section-heading ink-container">
          <button type="button" onClick={() => scrollBrands(-1)} aria-label="Previous brands">
            <FiChevronLeft aria-hidden="true" />
          </button>
          <div>
            <h2 id="maker-title">Shop By Brand</h2>
          </div>
          <button type="button" onClick={() => scrollBrands(1)} aria-label="Next brands">
            <FiChevronRight aria-hidden="true" />
          </button>
        </div>
        <div className="home-maker-rail" ref={brandRailRef}>
          {brandCollections.map((brand) => (
            <Link key={brand.name} to={`/shop?maker=${encodeURIComponent(brand.name)}`}>
              <img src={brand.image} alt="" loading="lazy" />
              <strong>{brand.name}</strong>
            </Link>
          ))}
        </div>
      </section>

      <section className="home-journal ink-container" id="journal" aria-labelledby="journal-title">
        <div className="home-section-title">
          <h2 id="journal-title">On the journal</h2>
        </div>
        <div className="home-journal__grid">
          {journalStories.map((story) => (
            <article key={story.title}>
              <img src={story.image} alt="" loading="lazy" />
              <h3>{story.title}</h3>
              <p>{story.text}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Main;
